import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Sparkles, Eye, Zap, Flame } from "lucide-react";
import { formatNumber } from "@/lib/format";
import PlatformBadge from "@/components/PlatformBadge";
import MediaImage from "@/components/MediaImage";
import { isLiveCreator } from "@/components/LiveDemoBadge";
import CompareColumn from "@/components/CompareColumn";
import TrendCluster from "@/components/TrendCluster";
import TrendBrief from "@/components/TrendBrief";
import MetricCard from "@/components/MetricCard";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Sun, Moon } from "lucide-react";

const ROWS = [
  ["videos", "Videos", (v) => v], ["cadence_per_week", "Cadence / wk", (v) => v], ["avg_views", "Avg views", formatNumber],
  ["median_views", "Median views", formatNumber], ["outliers", "Outliers", (v) => v], ["outlier_rate_pct", "Outlier rate", (v) => `${v}%`],
  ["peak_multiplier", "Peak lift", (v) => `${v}x`], ["avg_engagement_pct", "Engagement", (v) => `${v}%`],
];

export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { theme, toggle } = useTheme();

  useEffect(() => {
    axios.get(`${API}/share/${token}`).then(({ data }) => setData(data)).catch((e) => setError(e?.response?.data?.detail || "Report not found"));
  }, [token]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="shared-report-page">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-4 md:px-6 h-14 flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Sparkles className="h-4 w-4" /></div>
          <div className="font-display font-extrabold">OutlierPulse</div>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground border border-border rounded-full px-2 py-0.5 ml-1">Shared report</span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">{theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</Button>
            <Link to="/login" className="text-xs font-medium text-primary hover:underline" data-testid="shared-report-login-link">Open the app →</Link>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 md:px-6 py-8 space-y-6">
        {error ? (
          <Card className="p-10 text-center text-muted-foreground" data-testid="shared-report-error">{error}</Card>
        ) : !data ? (
          <div className="text-muted-foreground">Loading report…</div>
        ) : (
          <>
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="shared-report-title">{data.title}</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Snapshot taken {new Date(data.created_at).toLocaleString()}{data.created_by_name ? ` · shared by ${data.created_by_name}` : ""} · {data.views} view{data.views !== 1 ? "s" : ""}
              </p>
            </div>
            {data.kind === "compare" ? <CompareView s={data.snapshot} /> : <TrendView s={data.snapshot} />}
          </>
        )}
      </main>
    </div>
  );
}

function CompareView({ s }) {
  return (
    <div className="space-y-4" data-testid="shared-compare">
      <div className="grid grid-cols-2 gap-3">
        {[s.a, s.b].map((p) => (
          <Card key={p.creator.id} className="p-4 flex items-center gap-3">
            <MediaImage src={p.creator.avatar} alt="" name={p.creator.name} kind="avatar" demo={!isLiveCreator(p.creator)} className="h-12 w-12 rounded-full object-cover" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{p.creator.name}</div>
              <div className="text-xs text-muted-foreground truncate">{p.creator.handle} · {formatNumber(p.creator.followers)} followers</div>
            </div>
            <PlatformBadge platform={p.creator.platform} size="xs" />
          </Card>
        ))}
      </div>
      <Card className="p-5">
        <h3 className="font-display font-bold text-lg mb-3">Scorecard</h3>
        <div className="divide-y divide-border">
          {ROWS.map(([key, label, fmt]) => {
            const va = s.a.stats[key], vb = s.b.stats[key];
            return (
              <div key={key} className="grid grid-cols-[1fr_auto_1fr] items-center py-2 text-sm">
                <div className={`font-mono font-bold text-right pr-4 ${va > vb ? "text-[hsl(var(--emerald))]" : ""}`}>{fmt(va)}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground text-center w-32">{label}</div>
                <div className={`font-mono font-bold pl-4 ${vb > va ? "text-[hsl(var(--emerald))]" : ""}`}>{fmt(vb)}</div>
              </div>
            );
          })}
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CompareColumn profile={s.a} side="a" color="primary" />
        <CompareColumn profile={s.b} side="b" color="rose" />
      </div>
    </div>
  );
}

function TrendView({ s }) {
  const k = s.report.kpis;
  return (
    <div className="space-y-4" data-testid="shared-trend">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard label="Videos published" value={k.videos} icon={Eye} accent="primary" />
        <MetricCard label="Outliers detected" value={k.outliers} icon={Zap} accent="emerald" />
        <MetricCard label="Hottest hook" value={<span className="text-xl leading-tight block">{k.hottest_hook || "—"}</span>} icon={Flame} accent="rose" />
        <MetricCard label="Hottest topic" value={<span className="text-xl leading-tight block">{k.hottest_topic || "—"}</span>} icon={Sparkles} accent="amber" />
      </div>
      {s.brief && <Card className="p-5"><TrendBrief brief={s.brief} /></Card>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TrendCluster title="Hooks catching fire" subtitle="Ranked by views share × outlier density." clusters={s.report.hooks} accent="rose" testid="shared-hooks" />
        <TrendCluster title="Topics heating up" subtitle="Niches drawing the most attention." clusters={s.report.topics} accent="amber" testid="shared-topics" />
      </div>
    </div>
  );
}
